import { useState, useEffect } from 'react';
import { Package2, Plus, Trash2, Receipt, Sparkles, Eye, ShoppingCart, ChevronRight } from 'lucide-react';
import EmptyState from '../EmptyState';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { BookingMaterial } from '../../lib/bookings';
import { Material } from '../../lib/materials';
import SelectMaterialModal from '../manage/SelectMaterialModal';
import { useBusiness } from '../../contexts/BusinessContext';
import { ProductPhotoService } from '../../services/productPhotoService';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../lib/utils';
import { format } from 'date-fns';
import ExpenseDetailsModal from '../expenses/ExpenseDetailsModal';
import UploadReceiptModal from '../expenses/UploadReceiptModal';
import ExpenseReceiptPreview from '../expenses/ExpenseReceiptPreview';
import { getExpenseReceiptInfo } from '../../lib/expenseReceiptDisplay';
import AddManualExpenseModal from '../expenses/AddManualExpenseModal';
import { getBookingPurchaseOrderSummary, BookingPurchaseOrderSummary, Order } from '../../lib/purchaseOrders';
import PurchaseOrderDetailModal from '../PurchaseOrderDetailModal';

interface BookingMaterialsTabProps {
  materials: BookingMaterial[];
  onAddMaterial: (item: Material | any, quantity: number, source?: 'materials' | 'inventory') => Promise<void>;
  onRemoveMaterial: (id: string) => Promise<void>;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  onOpenMaterialModal: () => void;
  booking: { status: string };
  isInventoryAppInstalled?: boolean;
  isExpensesAppInstalled?: boolean;
  bookingId?: string;
  projectId?: string;
  businessId?: string;
}

export default function BookingMaterialsTab({
  materials = [],
  onAddMaterial,
  onRemoveMaterial,
  onUpdate,
  onError,
  onOpenMaterialModal,
  booking,
  isInventoryAppInstalled = false,
  isExpensesAppInstalled = false,
  bookingId,
  projectId,
  businessId
}: BookingMaterialsTabProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isSelectMaterialModalOpen, setIsSelectMaterialModalOpen] = useState(false);
  const [permissionLevel, setPermissionLevel] = useState<'manage' | 'view' | 'disabled'>('manage');
  const { selectedBusiness } = useBusiness();
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  
  // Expenses state
  const [expenses, setExpenses] = useState<any[]>([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<any | null>(null);
  const [isExpenseDetailsModalOpen, setIsExpenseDetailsModalOpen] = useState(false);
  const [isUploadReceiptModalOpen, setIsUploadReceiptModalOpen] = useState(false);
  const [isAddManualExpenseModalOpen, setIsAddManualExpenseModalOpen] = useState(false);
  const [manualExpensePreselection, setManualExpensePreselection] = useState<{
    bookingId?: string | null;
    projectId?: string | null;
  } | null>(null);
  const [categories, setCategories] = useState<any[]>([]);

  // Purchase orders that followed the customer journey to this booking.
  const [poSummary, setPoSummary] = useState<BookingPurchaseOrderSummary | null>(null);
  const [selectedPO, setSelectedPO] = useState<Order | null>(null);

  const loadPurchaseOrders = async () => {
    if (!bookingId || !businessId) return;
    try {
      const summary = await getBookingPurchaseOrderSummary(businessId, bookingId);
      setPoSummary(summary);
      // Keep an open detail modal in sync after receives/edits.
      setSelectedPO((prev) => (prev ? summary.orders.find((o) => o.id === prev.id) ?? null : null));
    } catch (err) {
      console.error('Error loading booking purchase orders:', err);
      setPoSummary(null);
    }
  };

  useEffect(() => {
    loadPurchaseOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, businessId]);

  const handleAddClick = () => {
    if (booking.status === 'completed') {
      onError('Cannot add materials to completed bookings');
      return;
    }
    setIsSelectMaterialModalOpen(true);
  };

  // Load product photos for inventory items
  useEffect(() => {
    const loadProductPhotos = async () => {
      const inventoryItems = materials.filter(m => m.item_id);
      if (inventoryItems.length === 0) {
        setProductPhotos({});
        return;
      }

      try {
        const stockItemIds = inventoryItems.map(m => m.item_id!).filter(Boolean);
        if (stockItemIds.length > 0) {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
          
          // Convert to the format expected by the component
          const photoUrls: Record<string, string> = {};
          if (photoMap && typeof photoMap === 'object') {
            Object.entries(photoMap).forEach(([itemId, photo]) => {
              if (photo && photo.photo_url) {
                photoUrls[itemId] = photo.photo_url;
              }
            });
          }
          
          setProductPhotos(photoUrls);
        }
      } catch (photoError) {
        console.error('Error loading product photos:', photoError);
        setProductPhotos({});
      }
    };

    loadProductPhotos();
  }, [materials]);

  // Fetch expenses for this booking or project
  useEffect(() => {
    if (isExpensesAppInstalled && (bookingId || projectId) && businessId) {
      fetchExpenses();
      fetchCategories();
    }
  }, [isExpensesAppInstalled, bookingId, projectId, businessId]);

  const fetchExpenses = async () => {
    if ((!bookingId && !projectId) || !businessId) return;
    
    setExpensesLoading(true);
    try {
      let query = supabase
        .from('expenses')
        .select(`
          *,
          category:expense_categories(name, color),
          receipts:expense_receipts(*)
        `)
        .eq('business_id', businessId);
      
      // Filter by booking_id or project_id
      if (bookingId) {
        query = query.eq('booking_id', bookingId);
      } else if (projectId) {
        query = query.eq('project_id', projectId);
      }
      
      const { data, error } = await query
        .order('expense_date', { ascending: false });

      if (error) throw error;
      setExpenses(data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      onError('Failed to load expenses');
    } finally {
      setExpensesLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!businessId) return;
    
    try {
      const { data, error } = await supabase
        .from('expense_categories')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('order_index');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const isMobile = window.innerWidth < 640;

  // MaterialIcon component for inventory items (matches ProductIcon from BookingProductList)
  const MaterialIcon = ({ itemId, materialName, isInventoryItem }: { itemId?: string | null; materialName: string; isInventoryItem: boolean }) => {
    if (!isInventoryItem || !itemId) {
      // Regular material - use smaller icon
      return (
        <div className={`${isMobile ? 'h-8 w-8' : 'h-9 w-9'} rounded-[10px] bg-blue-100 flex items-center justify-center flex-shrink-0`}>
          <Package2 className={`${isMobile ? 'h-4 w-4' : 'h-4.5 w-4.5'} text-blue-600`} />
        </div>
      );
    }

    const photoUrl = productPhotos[itemId];
    
    if (photoUrl) {
      return (
        <div className="h-14 w-14 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
          <img 
            src={photoUrl} 
            alt={materialName}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<div class="h-6 w-6 text-blue-600"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg></div>';
                parent.className = "h-14 w-14 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-14 w-14 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
        <Package2 className="h-6 w-6" />
      </div>
    );
  };

  return (
    <>
    <div 
      className="flex flex-col"
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
      }}
    >
      {/* Materials Section - Only show if Inventory app is installed */}
      {isInventoryAppInstalled && (
        <>
      {/* Header - iOS Style */}
      <div className={`bg-white border-b border-gray-200/60 ${isMobile ? 'px-3 py-2.5' : 'px-6 py-4'} flex justify-between items-center`}>
        <div className="flex items-center space-x-2">
          <h3 className={`${isMobile ? 'text-[16px]' : 'text-lg'} font-semibold text-gray-900`} style={{ 
            letterSpacing: '-0.01em'
          }}>
            Materials
          </h3>
          <span className={`${isMobile ? 'text-[12px]' : 'text-sm'} text-gray-500`}>
            ({materials.length})
          </span>
        </div>
            {permissionLevel === 'manage' && (
          <button
            onClick={handleAddClick}
                className={`inline-flex items-center ${isMobile ? 'px-2.5 py-1.5 text-[12px]' : 'px-3 py-1.5 text-xs'} font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 active:bg-blue-100 active:scale-95 rounded-[8px] transition-all duration-150 touch-manipulation`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
                <Plus className={`${isMobile ? 'h-3 w-3' : 'h-3.5 w-3.5'} mr-1`} />
                Add Materials/Inventory Items
          </button>
        )}
      </div>
      <div className={`flex-1 ${isMobile ? 'p-2' : 'p-4'}`}>
        {materials.length === 0 ? (
          <div className={`flex items-center justify-center ${isMobile ? 'py-16' : 'py-12'}`}>
            <EmptyState
              icon={<Package2 className={`${isMobile ? 'w-10 h-10' : 'w-8 h-8'} text-gray-400`} />}
              title="No materials added"
              description="Track materials used for this booking"
                  actionLabel={permissionLevel === 'manage' ? "Add Materials/Inventory Items" : "No materials added"}
              onAction={handleAddClick}
            />
          </div>
        ) : (
          <div className={`${isMobile ? 'space-y-2' : 'space-y-3'}`}>
            {materials.map((material) => {
              const isInventoryItem = !!material.item_id;
              // Use gray background for inventory items (matching products), white for regular materials
              const cardBgClass = isInventoryItem ? 'bg-gray-50' : 'bg-white border border-gray-200/60';
              const cardPadding = isMobile ? 'p-2.5' : 'p-4';
              
              return (
                <div 
                  key={material.id} 
                  className={`${cardBgClass} ${isInventoryItem ? 'rounded-lg' : 'rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.05)]'} overflow-hidden`}
                >
                  <div className={cardPadding}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <MaterialIcon 
                          itemId={material.item_id} 
                          materialName={material.material_name}
                          isInventoryItem={isInventoryItem}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-medium text-gray-900 truncate">{material.material_name}</h4>
                            {isInventoryItem && (
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 font-medium rounded">
                                From Inventory
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {Number(material.quantity).toFixed(2)} {material.unit} × ${Number(material.cost).toFixed(2)}
                          </p>
                          {permissionLevel === 'manage' && (
                            <button
                              onClick={() => {
                                if (booking.status === 'completed') {
                                  onError('Cannot remove materials from completed bookings');
                                  return;
                                }
                                setMaterialToDelete(material.id);
                                setIsDeleteModalOpen(true);
                              }}
                              disabled={booking.status === 'completed'}
                              className="flex items-center space-x-1 text-xs text-red-600 hover:text-red-800 mt-1 transition-colors"
                            >
                              <Trash2 className="h-3 w-3" />
                              <span>Remove</span>
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right flex-shrink-0 ml-4">
                        <div className="font-semibold text-gray-900">
                          ${(Number(material.quantity) * Number(material.cost)).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </div>
        </>
      )}

      {/* Purchase Orders — POs linked along the customer journey (lead/field
          quote/project) attach here automatically, so the business can see
          whether all materials for the job have been received. */}
      {poSummary && poSummary.orders.length > 0 && (
        <div className={`${isInventoryAppInstalled ? 'border-t border-gray-200' : ''} ${isMobile ? (isInventoryAppInstalled ? 'mt-4 pt-4' : 'mt-0 pt-0') : (isInventoryAppInstalled ? 'mt-6 pt-6' : 'mt-0 pt-0')} ${isMobile ? 'px-2' : 'px-4'}`}>
          <div className={`${isMobile ? 'mb-3' : 'mb-4'} flex items-center justify-between gap-2 flex-wrap`}>
            <div className="flex items-center space-x-2">
              <h3 className={`${isMobile ? 'text-[16px]' : 'text-lg'} font-semibold text-gray-900`} style={{ letterSpacing: '-0.01em' }}>
                Purchase Orders
              </h3>
              <span className={`${isMobile ? 'text-[12px]' : 'text-sm'} text-gray-500`}>
                ({poSummary.orders.length})
              </span>
            </div>
            {poSummary.totalQuantity > 0 && (
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${
                poSummary.allReceived ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {poSummary.receivedQuantity} of {poSummary.totalQuantity} items received
              </span>
            )}
          </div>
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden divide-y divide-gray-100 mb-4">
            {poSummary.orders.map((po) => {
              const poItems = po.items || [];
              const receivedCount = poItems.filter((i) => (i.received_quantity ?? 0) >= i.quantity).length;
              const quote = poItems.length > 0 && poItems.some((i) => !i.unit_price || i.unit_price <= 0);
              return (
                <div
                  key={po.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedPO(po)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedPO(po); } }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="h-8 w-8 rounded-[10px] bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-semibold text-gray-900 truncate">{po.order_number}</span>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${
                        quote ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-600'
                      }`}>
                        {quote ? 'Quote Request' : 'Purchase Order'}
                      </span>
                    </div>
                    <p className="text-[12px] text-gray-500 mt-0.5 truncate">
                      {`${po.supplier?.first_name ?? ''} ${po.supplier?.last_name ?? ''}`.trim() || 'Unknown Supplier'}
                      {poItems.length > 0 && ` · ${receivedCount} of ${poItems.length} received`}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[14px] font-bold text-gray-900">
                      {quote && po.total_amount === 0 ? 'TBD' : `$${po.total_amount.toFixed(2)}`}
                    </div>
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${
                      po.status === 'pending'
                        ? 'bg-orange-50 text-orange-700'
                        : po.status === 'received'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-red-50 text-red-700'
                    }`}>
                      {po.status === 'pending' ? 'Pending' : po.status === 'received' ? 'Received' : 'Cancelled'}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Expenses Section - Only show if Expenses app is installed */}
      {isExpensesAppInstalled && (
        <>
          <div className={`${isInventoryAppInstalled ? 'border-t border-gray-200' : ''} ${isMobile ? isInventoryAppInstalled ? 'mt-4 pt-4' : 'mt-0 pt-0' : isInventoryAppInstalled ? 'mt-6 pt-6' : 'mt-0 pt-0'}`}>
            <div className={`${isMobile ? 'mb-3' : 'mb-4'} flex items-center justify-between`}>
              <div className="flex items-center space-x-2">
                <h3 className={`${isMobile ? 'text-[16px]' : 'text-lg'} font-semibold text-gray-900`} style={{ 
                  letterSpacing: '-0.01em'
                }}>
                  Expenses
                </h3>
                <span className={`${isMobile ? 'text-[12px]' : 'text-sm'} text-gray-500`}>
                  ({expenses.length})
                </span>
              </div>
              {permissionLevel === 'manage' && booking.status !== 'completed' && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsUploadReceiptModalOpen(true)}
                    className={`inline-flex items-center ${isMobile ? 'px-2.5 py-1.5 text-[12px]' : 'px-3 py-1.5 text-xs'} font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 active:bg-blue-100 active:scale-95 rounded-[8px] transition-all duration-150 touch-manipulation`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Plus className={`${isMobile ? 'h-3 w-3' : 'h-3.5 w-3.5'} mr-1`} />
                    Upload Receipt
                  </button>
            <button
                    onClick={() => setIsAddManualExpenseModalOpen(true)}
                    className={`inline-flex items-center ${isMobile ? 'px-2.5 py-1.5 text-[12px]' : 'px-3 py-1.5 text-xs'} font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 active:bg-blue-100 active:scale-95 rounded-[8px] transition-all duration-150 touch-manipulation`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
                    <Plus className={`${isMobile ? 'h-3 w-3' : 'h-3.5 w-3.5'} mr-1`} />
                    Manual
            </button>
          </div>
        )}
      </div>

            {expensesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500">Loading expenses...</div>
              </div>
            ) : expenses.length === 0 ? (
              <div className={`flex items-center justify-center ${isMobile ? 'py-8' : 'py-6'}`}>
                <EmptyState
                  icon={<Receipt className={`${isMobile ? 'w-8 h-8' : 'w-6 h-6'} text-gray-400`} />}
                  title="No expenses assigned"
                  description="Expenses assigned to this booking will appear here"
                  actionLabel={permissionLevel === 'manage' && booking.status !== 'completed' ? "Add Expense" : undefined}
                  onAction={() => setIsUploadReceiptModalOpen(true)}
                />
              </div>
            ) : (
              <div className={`${isMobile ? 'space-y-2' : 'space-y-3'}`}>
                {expenses.map((expense) => (
                  <div
                    key={expense.id}
                    className="bg-white border border-gray-200/60 rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden"
                  >
                    <div className={isMobile ? 'p-2.5' : 'p-4'}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className={`${isMobile ? 'h-8 w-8' : 'h-9 w-9'} rounded-[10px] bg-emerald-100 flex items-center justify-center flex-shrink-0`}>
                            {expense.receipt_image_url ? (
                              <ExpenseReceiptPreview
                                {...getExpenseReceiptInfo(expense)}
                                url={expense.receipt_image_url}
                                variant="thumbnail"
                                className="w-full h-full"
                                imgClassName="w-full h-full rounded-[10px]"
                              />
                            ) : (
                              <Receipt className={`${isMobile ? 'h-4 w-4' : 'h-4.5 w-4.5'} text-emerald-600`} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-medium text-gray-900 truncate">{expense.description}</h4>
                              {expense.ai_categorized && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 flex-shrink-0">
                                  <Sparkles className="h-3 w-3" />
                                  Categorized by AI
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                              {expense.category && (
                                <span 
                                  className="px-2 py-0.5 rounded text-xs font-medium"
                                  style={{
                                    backgroundColor: expense.category.color ? `${expense.category.color}20` : '#e5e7eb',
                                    color: expense.category.color || '#6b7280'
                                  }}
                                >
                                  {expense.category.name}
                                </span>
                              )}
                              {expense.merchant_name && (
                                <>
                                  <span>•</span>
                                  <span className="truncate">{expense.merchant_name}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-gray-400 mt-1">
                              {expense.expense_date && (
                                <span>{format(new Date(expense.expense_date), 'MMM d, yyyy')}</span>
                              )}
                              {expense.tax_amount && expense.tax_amount > 0 && (
                                <>
                                  <span>•</span>
                                  <span>Tax: {formatCurrency(expense.tax_amount)}</span>
                                </>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                setSelectedExpense(expense);
                                setIsExpenseDetailsModalOpen(true);
                              }}
                              className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 mt-1 transition-colors"
                            >
                              <Eye className="h-3 w-3" />
                              <span>View Details</span>
                            </button>
                          </div>
                        </div>
                        
                        <div className="text-right flex-shrink-0 ml-4">
                          <div className="font-semibold text-gray-900">
                            {formatCurrency(expense.amount)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
    
    <DeleteConfirmationModal
      isOpen={isDeleteModalOpen}
      onClose={() => {
        setIsDeleteModalOpen(false);
        setMaterialToDelete(null);
      }}
      onConfirm={async () => {
        if (!materialToDelete) return;
        try {
          setDeleteLoading(true);
          await onRemoveMaterial(materialToDelete);
          setIsDeleteModalOpen(false);
          setMaterialToDelete(null);
        } finally {
          setDeleteLoading(false);
        }
      }}
      title="Remove Material"
      message="Are you sure you want to remove this material? This action cannot be undone."
      loading={deleteLoading}
    />
    <SelectMaterialModal
      isOpen={isSelectMaterialModalOpen}
      onClose={() => setIsSelectMaterialModalOpen(false)}
      onAdd={onAddMaterial}
      businessId={selectedBusiness?.id}
    />
    
    {/* Expense Details Modal */}
    {isExpensesAppInstalled && selectedExpense && (
      <ExpenseDetailsModal
        isOpen={isExpenseDetailsModalOpen}
        onClose={() => {
          setIsExpenseDetailsModalOpen(false);
          setSelectedExpense(null);
        }}
        expense={selectedExpense}
        onUpdate={async () => {
          await fetchExpenses();
          await onUpdate();
        }}
        onDelete={async (expenseId) => {
          try {
            const { error } = await supabase
              .from('expenses')
              .delete()
              .eq('id', expenseId);
            
            if (error) throw error;
            await fetchExpenses();
            await onUpdate();
            setIsExpenseDetailsModalOpen(false);
            setSelectedExpense(null);
          } catch (error) {
            console.error('Error deleting expense:', error);
            onError('Failed to delete expense');
          }
        }}
        categories={categories}
        onCategoriesChange={fetchCategories}
      />
    )}

    {/* Upload Receipt Modal */}
    {isExpensesAppInstalled && businessId && (
      <UploadReceiptModal
        isOpen={isUploadReceiptModalOpen}
        onClose={() => setIsUploadReceiptModalOpen(false)}
        onSave={async () => {
          await fetchExpenses();
          await onUpdate();
          setIsUploadReceiptModalOpen(false);
        }}
        businessId={businessId}
        categories={categories}
        onCategoriesChange={fetchCategories}
        initialBookingId={bookingId}
        initialProjectId={projectId}
        onSwitchToManual={(preselection) => {
          setIsUploadReceiptModalOpen(false);
          setManualExpensePreselection(preselection ?? null);
          setIsAddManualExpenseModalOpen(true);
        }}
      />
    )}

    {/* Add Manual Expense Modal */}
    {isExpensesAppInstalled && businessId && (
      <AddManualExpenseModal
        isOpen={isAddManualExpenseModalOpen}
        onClose={() => {
          setIsAddManualExpenseModalOpen(false);
          setManualExpensePreselection(null);
        }}
        onSave={async () => {
          await fetchExpenses();
          await onUpdate();
          setIsAddManualExpenseModalOpen(false);
          setManualExpensePreselection(null);
        }}
        businessId={businessId}
        categories={categories}
        onCategoriesChange={fetchCategories}
        initialBookingId={manualExpensePreselection?.bookingId ?? bookingId}
        initialProjectId={manualExpensePreselection?.projectId ?? projectId}
      />
    )}

    {/* Linked purchase order detail — receive items, record bills, etc. */}
    <PurchaseOrderDetailModal
      order={selectedPO}
      onClose={() => setSelectedPO(null)}
      onOrderUpdated={loadPurchaseOrders}
    />
    </>
  );
}